import React, { ReactElement, useContext, useState, useMemo, useRef, useEffect } from "react";
import {
    makeStyles,
    createStyles,
    TextField,
    Button,
    FormControlLabel,
    Checkbox,
    Radio,
    RadioGroup,
    Switch,
    CircularProgress,
    Typography,
    Dialog,
    DialogContent,
    IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SuccessDialog from "../../common/SuccessDialog";
import IdaChatbotIcon from "../../../images/ida-chatbot-icon.png";
import AIRewriteIcon from "../../../images/airewrite.png";
import IDAGenerateModal from "../../common/IDAGenerateModal";
import IDAOverwriteDialog from "../../common/IDAOverwriteDialog";
import { useFormik, getIn } from "formik";
import * as Yup from "yup";
import { v4 as uuidv4 } from "uuid";
import { Autocomplete } from "@material-ui/lab";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import { UserTypeContext } from "../../../UserTypeContext";
import {
    UserType,
    useCreateDiscountsMutation,
    WhereToRedeemEnum,
    DayEnum,
    useGetAllChamberMembersQuery,
} from "../../../graphql/types";
import useGetChamberMemberFromUserIdInAuth from "../../common/Hooks/useGetChamberMemberFromUserIdInAuth";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import useRedirect from "../../common/Hooks/useRedirect";
import useIsMemberProfileAndPerksBannersActiveForChamberMember from "../../common/Hooks/useCheckIfMemberProfileAndPerksBannersActiveForChamberMember";
import CSVButtons from "../../common/CSVButtons";
import { setDataAfterCSVImport, setPrivateEmails } from "../../common/PrivatePerk";
import { checkIfPerkNotificationIsEnabledForSelectedAvailability } from "../../common/PerkNotificationSwitch";
import { validWebsiteRegEx } from "../../common/utilities/regexes";
import ChamberMemberSelector from "./ChamberMemberSelector";
import theme from "../../../themes";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import { openInstructionPdf, openMemberInstructionPdf } from "../../common/openInstructionPdf";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import dayjs from "dayjs";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { goldDatePickerTheme } from "../../../themes";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import PerkBannerSection from "../../common/PerkBannerSection";
import NumberFormat from "react-number-format";
import { sharedPerkFormStyles } from "./perkFormStyles";
import UnsavedChangesPrompt from "../../common/UnsavedChangesPrompt";


const phoneRegExp = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;
const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const useStyles = makeStyles(() =>
    createStyles({
        ...sharedPerkFormStyles,
        // CreateMemberPerk-specific styles
        videoButtons: {
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "21px",
            marginTop: "10px",
            marginBottom: "10px",
            "@media (max-width: 600px)": {
                flexDirection: "column",
                alignItems: "stretch",
                gap: "8px",
                width: "100%",
                marginTop: "6px",
                "& > div": { width: "100%", margin: 0 },
                "& > div > a, & > div > a > button, & > div > button, & > button, & > a": {
                    width: "100%",
                    marginTop: "0 !important",
                    justifyContent: "center",
                },
            },
        },
        videoButton: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "#FFFFFF",
            color: theme.palette.primary.main,
            border: "none",
            borderRadius: "4px",
            padding: "12px 20px",

            fontWeight: 600,
            fontSize: "16px",
            lineHeight: "150%",
            cursor: "pointer",
            height: "48px",
        },
        optionHeading: {

            fontWeight: 700,
            fontSize: "18px",
            lineHeight: "24px",
            color: "#24272C",
            marginBottom: "8px",
            marginTop: "0",
        },
        optionText: {

            fontWeight: 400,
            fontSize: "18px",
            color: "#24272C",
            lineHeight: "24px",
            marginBottom: "16px",
            marginTop: "0",
            "& strong": {
                fontWeight: 700,
            },
        },
        redeemDetailsBox: {
            background: "#FFFFFF",
            border: "1px solid #CCCCCC",
            borderRadius: "12px",
            padding: "28px 28px 24px",
            marginTop: "28px",
            position: "relative" as const,
            width: "65%",
            boxSizing: "border-box" as const,
            [theme.breakpoints.down("sm")]: {
                width: "100%",
                padding: "24px 16px 20px",
            },
        },
        redeemCardPill: {
            position: "absolute" as const,
            top: -18,
            left: 20,
            background: theme.palette.primary.main,
            borderRadius: "50px",
            padding: "8px 20px",

            fontSize: "15px",
            fontWeight: 400,
            color: "#FFFFFF",
            whiteSpace: "nowrap" as const,
        },
        redeemFieldLabel: {

            fontWeight: 600,
            fontSize: "14px",
            color: "#333333",
            marginBottom: "8px",
            marginTop: "16px",
            "&:first-child": {
                marginTop: 0,
            },
        },
        redeemInput: {
            width: "100%",
            border: "1px solid #D0D0D0",
            borderRadius: "8px",
            padding: "14px 16px",

            fontSize: "14px",
            color: "#333333",
            outline: "none",
            boxSizing: "border-box" as const,
            "&::placeholder": {
                color: "#AAAAAA",
            },
        },
        redeemInputError: {
            borderColor: "#E53935 !important",
        },
        submitButton: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "15px 96px",
            gap: "10px",
            width: "363px",
            height: "50px",
            whiteSpace: "nowrap" as const,
            background: theme.palette.primary.main,
            borderRadius: "12px",
            [theme.breakpoints.down("sm")]: {
                width: "100%",
                padding: "15px 20px",
            },
            border: "none",

            fontStyle: "normal",
            fontWeight: 600,
            fontSize: "16px",
            lineHeight: "150%",
            color: "#FFFFFF",
            textTransform: "uppercase" as const,
            marginTop: "70px",
            marginLeft: "auto",
            marginRight: "auto",
            cursor: "pointer",
            "&:hover": {
                background: "#c99228",
            },
            "&:disabled": {
                background: theme.palette.primary.main,
                color: "#FFFFFF",
                opacity: 0.7,
                cursor: "not-allowed",
            },
        },
        // IDA Generate styles
        idaHelperContainer: {
            display: "flex",
            alignItems: "center",
            gap: "6px",
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: "10px",
            padding: "2px 5px",
            marginTop: "8px",
            backgroundColor: "#FFFFFF",
        },
        idaIcon: {
            width: "33px",
            height: "26px",
            objectFit: "contain" as const,
        },
        idaHelperText: {

            fontWeight: 700,
            fontSize: "12px",
            color: theme.palette.primary.main,
            lineHeight: "15px",
            flex: 1,
        },
        idaGenerateBtn: {
            backgroundColor: "#4CAF50",
            border: "1px solid #4CAF50",
            borderRadius: "10px",
            padding: "1px 13px",
            height: "17px",
            minWidth: "71px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            "&:hover": {
                backgroundColor: "#388E3C",
            },
        },
        idaGenerateBtnText: {

            fontWeight: 400,
            fontSize: "10px",
            color: "#FFFFFF",
            lineHeight: "15px",
        },
        overwriteDialogPaper: {
            borderRadius: "20px",
            maxWidth: "600px",
            width: "600px",
            position: "relative" as const,
            overflow: "hidden",
            backgroundColor: "#FFFFFF",
        },
        overwriteCloseButton: {
            position: "absolute" as const,
            right: "12px",
            top: "12px",
            backgroundColor: (theme.palette as any).delete.main,
            color: "white",
            width: "30px",
            height: "30px",
            padding: 0,
            zIndex: 1,
            "&:hover": { backgroundColor: "#D41920" },
        },
        overwriteIconCircle: {
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            backgroundColor: theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px auto",
        },
        overwriteTitle: {

            fontWeight: 700,
            fontSize: "14px",
            lineHeight: "15px",
            textAlign: "center" as const,
            color: "#000000",
            marginBottom: "8px",
        },
        overwriteDesc: {

            fontSize: "12px",
            lineHeight: "20px",
            textAlign: "center" as const,
            color: theme.palette.text.primary,
            fontWeight: 400,
            marginBottom: "16px",
            padding: "0 50px",
        },
        overwritePreviewBox: {
            border: `1px solid ${theme.palette.success.main}`,
            borderRadius: "6px",
            padding: "12px 6px",
            margin: "0 50px",
            maxHeight: "200px",
            overflow: "auto",
        },
        overwritePreviewLabel: {

            fontSize: "13px",
            fontWeight: 700,
            color: theme.palette.success.main,
            marginBottom: "8px",
        },
        overwritePreviewText: {

            fontSize: "13px",
            color: theme.palette.text.primary,
            lineHeight: "1.6",
        },
        overwriteButtons: {
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            margin: "20px 0 24px 0",
        },
        overwriteRegenBtn: {
            backgroundColor: theme.palette.success.main,
            color: "#FFFFFF",

            fontWeight: 700,
            fontSize: "14px",
            borderRadius: "8px",
            padding: "10px 24px",
            textTransform: "uppercase" as const,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            "&:hover": { backgroundColor: "#009A1B" },
        },
        overwriteConfirmBtn: {
            backgroundColor: theme.palette.primary.main,
            color: "#FFFFFF",

            fontWeight: 700,
            fontSize: "14px",
            borderRadius: "8px",
            padding: "10px 32px",
            textTransform: "uppercase" as const,
            "&:hover": { backgroundColor: "#C99028" },
        },
        idaSuccessDialogPaper: {
            borderRadius: "20px",
            maxWidth: "420px",
            width: "420px",
            overflow: "hidden",
            backgroundColor: "#FFFFFF",
        },
        idaSuccessIconCircle: {
            width: "90px",
            height: "90px",
            borderRadius: "50%",
            backgroundColor: "#22C55E",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px auto",
        },
        idaSuccessTitle: {

            fontWeight: 700,
            fontSize: "20px",
            textAlign: "center" as const,
            color: "#000",
            lineHeight: "1.5",
        },
        idaGenerateButton: {
            backgroundColor: "#4CAF50",
            border: "1px solid #4CAF50",
            borderRadius: "10px",
            padding: "1px 13px",
            height: "17px",
            minWidth: "71px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            "&:hover": {
                backgroundColor: "#388E3C",
            },
        },
        idaGenerateButtonText: {
            fontWeight: 400,
            fontSize: "10px",
            color: "#FFFFFF",
            lineHeight: "15px",
        },
    })
);

const CreateMemberPerk = (): ReactElement => {
    const classes = useStyles();
    const enumStrings = useEnumStrings();
    const { state: { userType } } = useContext(UserTypeContext);
    const [chamberMemberIdSelectedByChamberAdmin, setChamberMemberIdSelectedByChamberAdmin] = useState<string | null>(null);
    const [chamberMemberError, setChamberMemberError] = useState(false);

    const userIdInAuthChamberMemberInfo = useGetChamberMemberFromUserIdInAuth();
    const chamberInfo = useGetChamberFromUserIdInAuth();

    const { data: allChamberMemberData } = useGetAllChamberMembersQuery({
        variables: { chamberId: chamberInfo?.id || "" },
        skip: !chamberInfo?.id || userType !== UserType.ChamberAdmin,
        fetchPolicy: "cache-and-network",
    });

    const chamberMemberInfo = useMemo(() => {
        if (userType === UserType.ChamberAdmin) {
            if (chamberMemberIdSelectedByChamberAdmin && allChamberMemberData?.getAllChamberMembers) {
                return allChamberMemberData.getAllChamberMembers.find(m => m.id === chamberMemberIdSelectedByChamberAdmin);
            }
            return undefined;
        }
        return userIdInAuthChamberMemberInfo;
    }, [userType, chamberMemberIdSelectedByChamberAdmin, allChamberMemberData, userIdInAuthChamberMemberInfo]);

    const [createDiscount] = useCreateDiscountsMutation();
    const [loader, setLoader] = useState(false);

    // Date picker refs
    const startDateInputRef = useRef<HTMLInputElement>(null);
    const endDateInputRef = useRef<HTMLInputElement>(null);

    const openDatePicker = (ref: React.RefObject<HTMLInputElement>) => {
        if (!ref.current) return;
        try {
            (ref.current as any).showPicker();
        } catch {
            ref.current.click();
        }
    };

    // IDA Generate state
    const [idaDialogOpen, setIdaDialogOpen] = useState(false);
    const [currentIdaField, setCurrentIdaField] = useState<"title" | "description">("title");
    const [idaKeyDetails, setIdaKeyDetails] = useState("");
    const [idaGeneratedText, setIdaGeneratedText] = useState("");
    const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
    const [idaSuccessDialogOpen, setIdaSuccessDialogOpen] = useState(false);
    const [idaGeneratedTitle, setIdaGeneratedTitle] = useState(false);
    const [idaGeneratedDescription, setIdaGeneratedDescription] = useState(false);
    const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);

    const redirectToDiscountList = useRedirect(
        userType === UserType.ChamberAdmin
            ? RedirectUri.memberDiscountPageChamberAdmin
            : RedirectUri.manageDiscountChamberMemberAdmin,
        { showToast: true, showSuccessDialog: "created" }
    );

    const isFeaturedMember = useIsMemberProfileAndPerksBannersActiveForChamberMember({
        activeMemberProfileAndPerksBannersSettings: chamberMemberInfo?.activeMemberProfileAndPerksBannersSettings,
    });

    const initialValues = {
        title: "",
        description: "",
        isReoccuring: true,
        isAvailableToAllMembers: false,
        memberOnly: false,
        isPrivate: false,
        privateEmails: [] as string[],
        daysAvailable: [] as DayEnum[],
        startTimeUtcMilli: null as number | null,
        expiryTimeUtcMilli: null as number | null,
        redeemSettings: {
            whereToRedeem: WhereToRedeemEnum.InPerson,
            howToRedeem: {
                code: "",
                contactEmail: "",
                contactNumber: "",
                contactName: "",
                websiteToEnterCodeAt: "",
            },
        },
        notify: true,
        isExpiryEnabled: false,
        isCertainDaysEnabled: false,
        isCertainMonthsEnabled: false,
        startDate: "",
        endDate: "",
        banner: undefined as any,
    };

    const validationSchema = Yup.object().shape({
        title: Yup.string().required(enumStrings.validation.perkTitleIsRequired).max(50, enumStrings.validation.maxCharacters50),
        description: Yup.string().required(enumStrings.validation.perkDescriptionIsRequired).max(250, enumStrings.validation.maxCharacters250),
        memberOnly: Yup.boolean(),
        isAvailableToAllMembers: Yup.boolean(),
        isPrivate: Yup.boolean(),
        isReoccuring: Yup.boolean().required(enumStrings.validation.selectRedemptionOption),
        startDate: Yup.string().when("isExpiryEnabled", {
            is: true,
            then: Yup.string().required(enumStrings.validation.startDateRequired),
        }),
        endDate: Yup.string().when("isExpiryEnabled", {
            is: true,
            then: Yup.string().required(enumStrings.validation.endDateRequired),
        }),
        redeemSettings: Yup.object().shape({
            whereToRedeem: Yup.string().required(enumStrings.validation.selectWhereToRedeem),
            howToRedeem: Yup.object().when("whereToRedeem", {
                is: (val: WhereToRedeemEnum) => val === WhereToRedeemEnum.Online,
                then: Yup.object().shape({
                    code: Yup.string().required(enumStrings.validation.codeIsRequired),
                    websiteToEnterCodeAt: Yup.string().matches(validWebsiteRegEx, enumStrings.validation.enterValidWebsite).required(enumStrings.validation.urlIsRequired),
                }),
            }).when("whereToRedeem", {
                is: (val: WhereToRedeemEnum) => val === WhereToRedeemEnum.ContactCompany,
                then: Yup.object().shape({
                    contactName: Yup.string().required(enumStrings.validation.contactNameIsRequired),
                    contactEmail: Yup.string().matches(emailRegex, enumStrings.validation.enterValidEmail).required(enumStrings.validation.contactEmailIsRequired || "Contact Email is required"),
                    contactNumber: Yup.string().min(10, enumStrings.validation.enterValidPhoneNumber || "Enter a valid phone number").required(enumStrings.validation.contactNumberIsRequired || "Contact Number is required"),
                }),
            }),
        }),
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: async (values, { resetForm }) => {
            setLoader(true);
            try {
                const perkId = uuidv4();
                const startTime = values.startDate ? new Date(values.startDate).getTime() : null;
                const expiryTime = values.endDate ? new Date(values.endDate).getTime() : null;

                const isNotificationEnabled = checkIfPerkNotificationIsEnabledForSelectedAvailability({
                    daysAvailable: values.daysAvailable,
                    monthsAvailable: null,
                    startTimeUtcMilli: startTime,
                    expiryTimeUtcMilli: expiryTime,
                });

                const response = await createDiscount({
                    variables: {
                        input: {
                            data: [
                                {
                                    id: perkId,
                                    chamberId: chamberMemberInfo?.chamberId || "",
                                    chamberMemberId: chamberMemberInfo?.id || "",
                                    title: values.title.trim(),
                                    description: values.description?.trim(),
                                    isReoccuring: values.isReoccuring,
                                    memberOnly: values.isAvailableToAllMembers || values.memberOnly,
                                    isAvailableToAllMembers: values.isAvailableToAllMembers,
                                    isPrivate: values.isPrivate,
                                    privateEmails: values.isPrivate ? values.privateEmails : undefined,
                                    daysAvailable: values.isCertainDaysEnabled ? values.daysAvailable : undefined,
                                    startTimeUtcMilli: (values.isExpiryEnabled && startTime && expiryTime) ? startTime : undefined,
                                    expiryTimeUtcMilli: (values.isExpiryEnabled && startTime && expiryTime) ? expiryTime : undefined,
                                    redeemSettings: {
                                        whereToRedeem: values.redeemSettings.whereToRedeem,
                                        howToRedeem: values.redeemSettings.whereToRedeem !== WhereToRedeemEnum.InPerson
                                            ? values.redeemSettings.howToRedeem
                                            : undefined,
                                    },
                                    notify: isNotificationEnabled ? values.notify : false,
                                    banner: values.banner || undefined,
                                }
                            ]
                        }
                    },
                    refetchQueries: [
                        "getAllDiscountsFromChamberMemberId",
                        "getAllDiscountsFromChamberId",
                    ],
                });

                if (response.data?.createDiscounts.discountsAddedSuccessfully) {
                    resetForm({ values });
                    setSubmittedSuccessfully(true);
                }
            } catch (err) {
                console.error("Error creating perk:", err);
            } finally {
                setLoader(false);
            }
        },
    });

    // Scroll to first validation error on submit
    useEffect(() => {
        if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
            const firstErrorElement = document.querySelector('[data-error="true"]');
            if (firstErrorElement) {
                firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    }, [formik.submitCount, formik.errors]);

    // Redirect AFTER UnsavedChangesPrompt's history.block has been released
    // (dirty-flag flip happens in a re-render before this effect fires)
    useEffect(() => {
        if (submittedSuccessfully) {
            redirectToDiscountList();
        }
    }, [submittedSuccessfully, redirectToDiscountList]);

    const handleDayToggle = (day: DayEnum) => {
        const currentDays = formik.values.daysAvailable || [];
        if (currentDays.includes(day)) {
            formik.setFieldValue("daysAvailable", currentDays.filter(d => d !== day));
        } else {
            formik.setFieldValue("daysAvailable", [...currentDays, day]);
        }
    };

    const showFutureStartDateNotification =
        !!formik.values.isExpiryEnabled &&
        !!formik.values.startDate &&
        new Date(formik.values.startDate).getTime() > Date.now();

    const idaFieldMeta = useMemo(() => {
        if (currentIdaField === "title") {
            return {
                mode: "perkTitle" as const,
                maxLength: 50,
                title: enumStrings.createPerk.idaTitleModalTitle,
                subtitle: enumStrings.createPerk.idaTitleModalSubtitle,
                subtitleLine2: "",
                placeholder: enumStrings.createPerk.idaTitleModalPlaceholder,
                overwriteTitle: enumStrings.createPerk.idaTitleOverwriteTitle,
                overwriteDesc: enumStrings.createPerk.idaTitleOverwriteDesc,
                generatedLabel: enumStrings.createPerk.idaTitleGeneratedLabel,
                successTitle: enumStrings.createPerk.idaTitleSuccessTitle,
                successTitle2: enumStrings.createPerk.idaTitleSuccessTitle2,
            };
        }

        return {
            mode: "perkDescription" as const,
            maxLength: 250,
            title: enumStrings.createPerk.idaDescModalTitle,
            subtitle: enumStrings.createPerk.idaDescModalSubtitle,
            subtitleLine2: "",
            placeholder: enumStrings.createPerk.idaDescModalPlaceholder,
            overwriteTitle: enumStrings.createPerk.idaDescOverwriteTitle,
            overwriteDesc: enumStrings.createPerk.idaDescOverwriteDesc,
            generatedLabel: enumStrings.createPerk.idaDescGeneratedLabel,
            successTitle: enumStrings.createPerk.idaDescSuccessTitle,
            successTitle2: enumStrings.createPerk.idaDescSuccessTitle2,
        };
    }, [currentIdaField, enumStrings]);

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.headerTopRow}>
                    <div className={classes.pageTitle} style={{ flexDirection: "column", alignItems: "flex-start" }}>
                        <span>{enumStrings.createPerk.pageTitle}{chamberMemberInfo?.name ? ` ${enumStrings.createPerk.forText} ${chamberMemberInfo.name}` : ""}</span>
                        <span className={classes.pageSubtitle}>
                            {enumStrings.createPerk.pageSubtitle}
                        </span>
                    </div>
                </div>
                <div className={classes.videoButtons}>
                    <VideoTutorialButton
                        tutorialUrl="https://share.synthesia.io/embeds/videos/2b53e93e-018e-4d54-9311-19f78ebacd9a"
                    />
                    <ExportButton
                        customOnClick={() =>
                            userType === UserType.ChamberMemberAdmin
                                ? openMemberInstructionPdf("Member Dashboard - Create a Perk.pdf")
                                : openInstructionPdf("Create Member Perks.pdf")
                        }
                        disablePadding
                        icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
                        label="VIEW INSTRUCTIONS"
                    />
                </div>
            </div>

            <div className={classes.contentContainer}>
                <div className={classes.contentCard}>
                    <div className={classes.formFrame}>
                        <form onSubmit={(e) => {
                            if (userType === UserType.ChamberAdmin && !chamberMemberIdSelectedByChamberAdmin) {
                                setChamberMemberError(true);
                            } else {
                                setChamberMemberError(false);
                            }
                            formik.handleSubmit(e);
                        }} className={classes.form}>
                            {/* Member Company Name Section */}
                            {userType === UserType.ChamberAdmin && (
                                <div className={classes.fieldContainer}>
                                    <div className={classes.fieldLabel}>
                                        {enumStrings.ChamberMemberSelector.memberCompanyName}
                                        <span className={classes.requiredAsterisk}>*</span>
                                    </div>
                                    <p className={classes.helperText}>
                                        {enumStrings.selectMemberCompany}
                                    </p>
                                    <ChamberMemberSelector
                                        chamberMemberId={chamberMemberIdSelectedByChamberAdmin}
                                        setChamberMemberId={(id) => {
                                            setChamberMemberIdSelectedByChamberAdmin(id);
                                            if (id) setChamberMemberError(false);
                                        }}
                                        allChamberMemberData={allChamberMemberData}
                                        hideTooltip
                                        fullWidth
                                        hideLabel
                                        error={chamberMemberError}
                                    />
                                    {chamberMemberError && (
                                        <div data-error="true" className={classes.redeemErrorText}>{enumStrings.validation.memberCompanyNameIsRequired}</div>
                                    )}
                                </div>
                            )}

                            {/* Perk Description Section */}
                            <div className={classes.fieldContainer}>
                                <div className={classes.fieldLabelRow}>
                                    <div className={classes.fieldLabel}>
                                        {enumStrings.createPerk.perkDescription}
                                        <span className={classes.requiredAsterisk}>*</span>
                                    </div>
                                </div>
                                <p className={classes.helperText} dangerouslySetInnerHTML={{ __html: enumStrings.createPerk.perkDescriptionHelperText }} />
                                {/* Custom textarea wrapper with IDA bar inside */}
                                <div style={{
                                    border: (formik.submitCount > 0 || formik.touched.description) && formik.errors.description
                                        ? `2px solid ${(theme.palette as any).delete.main}`
                                        : idaGeneratedDescription
                                            ? `2px solid ${theme.palette.success.main}`
                                            : "1px solid #2C2C2C",
                                    borderRadius: "8px",
                                    backgroundColor: "#FFFFFF",
                                    overflow: "hidden",
                                    width: "100%",
                                }}>
                                    {idaGeneratedDescription && (
                                        <div style={{
                                            padding: "8px 16px 0 16px",
                                            fontFamily: "DM Sans, sans-serif",
                                            fontWeight: 700,
                                            fontSize: "12px",
                                            lineHeight: "24px",
                                            color: theme.palette.success.main,
                                        }}>
                                            {enumStrings.createPerk.idaPerkDescriptionLabel}
                                        </div>
                                    )}
                                    <textarea
                                        style={{
                                            width: "100%",
                                            minHeight: "180px",
                                            padding: "16px",
                                            fontSize: "16px",
                                            fontWeight: 400,
                                            color: "#000000",
                                            border: "none",
                                            outline: "none",
                                            boxSizing: "border-box" as const,
                                            resize: "none" as const,
                                            backgroundColor: "#FFFFFF",
                                            display: "block",
                                        }}
                                        placeholder={enumStrings.createPerk.perkDescriptionPlaceholder}
                                        name="description"
                                        value={formik.values.description}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            if (idaGeneratedDescription) setIdaGeneratedDescription(false);
                                        }}
                                        onBlur={formik.handleBlur}
                                        rows={6}
                                        maxLength={250}
                                    />
                                    {/* Footer row inside the border: counter left, IDA + Generate right */}
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "8px 12px", borderTop: "1px solid #E5E5E5", backgroundColor: "#FFFFFF", flexWrap: "wrap" }}>
                                        <span style={{ fontSize: "13px", color: "#6B6B6B", fontFamily: "DM Sans, sans-serif" }}>
                                            {(formik.values.description?.length || 0)}/250 {enumStrings.createPerk.characters}
                                        </span>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <img src={IdaChatbotIcon} alt="IDA" style={{ width: "32px", height: "32px", objectFit: "contain" }} />
                                            {chamberInfo?.isSandbox ? (
                                                <Button
                                                    variant="contained"
                                                    onClick={() => {
                                                        setCurrentIdaField("description");
                                                        setIdaKeyDetails(formik.values.description || "");
                                                        setIdaDialogOpen(true);
                                                    }}
                                                    style={{ backgroundColor: "#4CAF50", color: "#FFFFFF", fontSize: "13px", fontWeight: 700, borderRadius: "8px", padding: "6px 18px", textTransform: "uppercase", minHeight: "32px", boxShadow: "none" }}
                                                >
                                                    {idaGeneratedDescription ? enumStrings.idaGenerate.idaRegenerate : enumStrings.idaGenerate.generateButton}
                                                </Button>
                                            ) : (
                                                <span style={{ backgroundColor: "#E53935", color: "#FFFFFF", border: "1px solid #E53935", borderRadius: "10px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap" as const, display: "inline-block" }}>
                                                    COMING SOON
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {(formik.submitCount > 0 || formik.touched.description) && formik.errors.description && (
                                    <div data-error="true" className={classes.redeemErrorText}>{formik.errors.description}</div>
                                )}
                            </div>

                            {/* Perk Title Section */}
                            <div className={classes.fieldContainer}>
                                <div className={classes.fieldLabelRow}>
                                    <div className={classes.fieldLabel}>
                                        {enumStrings.createPerk.perkTitle}
                                        <span className={classes.requiredAsterisk}>*</span>
                                    </div>
                                </div>
                                <p className={classes.helperText} dangerouslySetInnerHTML={{ __html: enumStrings.createPerk.perkTitleHelperText }} />
                                {/* Custom input wrapper with IDA bar inside */}
                                <div style={{
                                    border: (formik.submitCount > 0 || formik.touched.title) && formik.errors.title
                                        ? `2px solid ${(theme.palette as any).delete.main}`
                                        : idaGeneratedTitle
                                            ? `2px solid ${theme.palette.success.main}`
                                            : "1px solid #2C2C2C",
                                    borderRadius: "8px",
                                    backgroundColor: "#FFFFFF",
                                    overflow: "hidden",
                                    width: "100%",
                                }}>
                                    {idaGeneratedTitle && (
                                        <div style={{
                                            padding: "8px 16px 0 16px",
                                            fontFamily: "DM Sans, sans-serif",
                                            fontWeight: 700,
                                            fontSize: "12px",
                                            lineHeight: "24px",
                                            color: theme.palette.success.main,
                                        }}>
                                            {enumStrings.createPerk.idaPerkTitleLabel}
                                        </div>
                                    )}
                                    <input
                                        style={{
                                            width: "100%",
                                            height: "56px",
                                            padding: "14px 16px",
                                            fontSize: "16px",
                                            fontWeight: 400,
                                            color: "#000000",
                                            border: "none",
                                            outline: "none",
                                            boxSizing: "border-box" as const,
                                            backgroundColor: "#FFFFFF",
                                        }}
                                        placeholder={enumStrings.createPerk.perkTitlePlaceholder}
                                        name="title"
                                        value={formik.values.title}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            if (idaGeneratedTitle) setIdaGeneratedTitle(false);
                                        }}
                                        onBlur={formik.handleBlur}
                                        maxLength={50}
                                    />
                                    {/* Footer row inside the border: counter left, IDA + Generate right */}
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "8px 12px", borderTop: "1px solid #E5E5E5", backgroundColor: "#FFFFFF", flexWrap: "wrap" }}>
                                        <span style={{ fontSize: "13px", color: "#6B6B6B", fontFamily: "DM Sans, sans-serif" }}>
                                            {(formik.values.title?.length || 0)}/50 {enumStrings.createPerk.characters}
                                        </span>
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <img src={IdaChatbotIcon} alt="IDA" style={{ width: "32px", height: "32px", objectFit: "contain" }} />
                                            {chamberInfo?.isSandbox ? (
                                                <Button
                                                    variant="contained"
                                                    onClick={() => {
                                                        setCurrentIdaField("title");
                                                        setIdaKeyDetails(formik.values.title || "");
                                                        setIdaDialogOpen(true);
                                                    }}
                                                    style={{ backgroundColor: "#4CAF50", color: "#FFFFFF", fontSize: "13px", fontWeight: 700, borderRadius: "8px", padding: "6px 18px", textTransform: "uppercase", minHeight: "32px", boxShadow: "none" }}
                                                >
                                                    {idaGeneratedTitle ? enumStrings.idaGenerate.idaRegenerate : enumStrings.idaGenerate.generateButton}
                                                </Button>
                                            ) : (
                                                <span style={{ backgroundColor: "#E53935", color: "#FFFFFF", border: "1px solid #E53935", borderRadius: "10px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap" as const, display: "inline-block" }}>
                                                    COMING SOON
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {(formik.submitCount > 0 || formik.touched.title) && formik.errors.title && (
                                    <div data-error="true" className={classes.redeemErrorText}>{formik.errors.title}</div>
                                )}
                            </div>

                            {/* Perk Banners Section */}
                            <div style={{ marginBottom: "30px" }}>
                                <PerkBannerSection
                                    isFeaturedMember={isFeaturedMember}
                                    formik={formik}
                                    isCreateMode={true}
                                    isChamberAdmin={userType === UserType.ChamberAdmin}
                                />
                            </div>

                            {/* Perk Redeemable By Section */}
                            <div className={classes.fieldContainer}>
                                <div className={classes.fieldLabelRow}>
                                    <div className={classes.fieldLabel}>
                                        {enumStrings.discountText.availableToText}
                                        <span className={classes.requiredAsterisk}>*</span>
                                    </div>
                                </div>
                                <p className={classes.helperText} dangerouslySetInnerHTML={{ __html: enumStrings.createPerk.redeemableByHelperText }} />
                                <RadioGroup
                                    className={classes.radioGroup}
                                    value={formik.values.isPrivate ? "private" : (formik.values.isAvailableToAllMembers ? "all" : (formik.values.memberOnly ? "local" : "anyone"))}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        formik.setFieldValue("isPrivate", val === "private");
                                        formik.setFieldValue("isAvailableToAllMembers", val === "all");
                                        formik.setFieldValue("memberOnly", val === "local");
                                    }}
                                >
                                    <FormControlLabel
                                        value="anyone"
                                        control={<Radio color="primary" icon={<span style={{ width: 22, height: 22, border: "2px solid #CCCCCC", borderRadius: "50%", display: "inline-block", boxSizing: "border-box" }} />} checkedIcon={<span style={{ width: 22, height: 22, border: `2px solid ${theme.palette.primary.main}`, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.palette.primary.main, boxSizing: "border-box" }}><span style={{ width: 8, height: 8, backgroundColor: "#FFFFFF", borderRadius: "50%" }} /></span>} />}
                                        label={
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: "16px", color: "#000000" }}>
                                                    {enumStrings.createPerk.anyoneRecommended}
                                                </div>
                                                <div style={{ fontWeight: 400, fontSize: "14px", color: theme.palette.text.secondary, marginTop: "2px" }}>
                                                    {enumStrings.createPerk.anyoneDesc}
                                                </div>
                                            </div>
                                        }
                                        className={classes.radioLabel}
                                    />
                                    <FormControlLabel
                                        value="local"
                                        control={<Radio color="primary" icon={<span style={{ width: 22, height: 22, border: "2px solid #CCCCCC", borderRadius: "50%", display: "inline-block", boxSizing: "border-box" }} />} checkedIcon={<span style={{ width: 22, height: 22, border: `2px solid ${theme.palette.primary.main}`, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.palette.primary.main, boxSizing: "border-box" }}><span style={{ width: 8, height: 8, backgroundColor: "#FFFFFF", borderRadius: "50%" }} /></span>} />}
                                        label={
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: "16px", color: "#000000" }}>
                                                    {enumStrings.discountText.radioGroupText.localChamberMembers}
                                                </div>
                                                <div style={{ fontWeight: 400, fontSize: "14px", color: theme.palette.text.secondary, marginTop: "2px" }}>
                                                    {enumStrings.createPerk.localMembersDesc}
                                                </div>
                                            </div>
                                        }
                                        className={classes.radioLabel}
                                    />
                                    <FormControlLabel
                                        value="all"
                                        control={<Radio color="primary" icon={<span style={{ width: 22, height: 22, border: "2px solid #CCCCCC", borderRadius: "50%", display: "inline-block", boxSizing: "border-box" }} />} checkedIcon={<span style={{ width: 22, height: 22, border: `2px solid ${theme.palette.primary.main}`, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.palette.primary.main, boxSizing: "border-box" }}><span style={{ width: 8, height: 8, backgroundColor: "#FFFFFF", borderRadius: "50%" }} /></span>} />}
                                        label={
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: "16px", color: "#000000" }}>
                                                    {enumStrings.discountText.radioGroupText.allChamberMembers}
                                                </div>
                                                <div style={{ fontWeight: 400, fontSize: "14px", color: theme.palette.text.secondary, marginTop: "2px" }}>
                                                    {enumStrings.createPerk.allMembersDesc}
                                                </div>
                                            </div>
                                        }
                                        className={classes.radioLabel}
                                    />
                                    <FormControlLabel
                                        value="private"
                                        control={<Radio color="primary" icon={<span style={{ width: 22, height: 22, border: "2px solid #CCCCCC", borderRadius: "50%", display: "inline-block", boxSizing: "border-box" }} />} checkedIcon={<span style={{ width: 22, height: 22, border: `2px solid ${theme.palette.primary.main}`, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.palette.primary.main, boxSizing: "border-box" }}><span style={{ width: 8, height: 8, backgroundColor: "#FFFFFF", borderRadius: "50%" }} /></span>} />}
                                        label={
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: "16px", color: "#000000" }}>
                                                    {enumStrings.discountText.radioGroupText.private}
                                                </div>
                                                <div style={{ fontWeight: 400, fontSize: "14px", color: theme.palette.text.secondary, marginTop: "2px" }}>
                                                    {enumStrings.createPerk.employeesOnlyDesc}
                                                </div>
                                            </div>
                                        }
                                        className={classes.radioLabel}
                                    />
                                </RadioGroup>

                                {/* Private Perk - Companies Employee Card */}
                                {formik.values.isPrivate && (
                                    <div className={classes.privatePerkCard}>
                                        <div className={classes.privatePerkCardHeader}>
                                            <p className={classes.privatePerkCardTitle}>
                                                {enumStrings.createPerk.companiesEmployeeOnly}
                                            </p>
                                        </div>

                                        {/* How to Set Up */}
                                        <div className={classes.howToSetUpBox}>
                                            <div className={classes.howToSetUpTitle}>How to Set Up</div>

                                            {/* Option 1 */}
                                            <p className={classes.optionHeading}>{enumStrings.createPerk.option1}</p>
                                            <p className={classes.optionText}>
                                                {enumStrings.createPerk.option1Text1}
                                            </p>
                                            <p className={classes.optionText} dangerouslySetInnerHTML={{ __html: enumStrings.createPerk.option1Text2 }} />

                                            {/* Option 2 */}
                                            <p className={classes.optionHeading}>{enumStrings.createPerk.option2}</p>
                                            <p className={classes.optionText} dangerouslySetInnerHTML={{ __html: enumStrings.createPerk.option2Text1 }} />
                                            <p className={classes.optionText}>
                                                {enumStrings.createPerk.option2Text2}
                                            </p>

                                            {/* Buttons */}
                                            <div className={classes.csvButtonsContainer}>
                                                <CSVButtons
                                                    setDataAfterCSVImport={(data) =>
                                                        setDataAfterCSVImport(data, (emailsToSet) =>
                                                            formik.setFieldValue("privateEmails", emailsToSet)
                                                        )
                                                    }
                                                />
                                                <Button
                                                    variant="outlined"
                                                    style={{
                                                        color: theme.palette.primary.main,
                                                        borderColor: theme.palette.primary.main,
                                                        fontSize: "14px",
                                                        fontWeight: 600,
                                                        padding: "12px 20px",
                                                        borderRadius: "12px",
                                                        textTransform: "none",
                                                    }}
                                                    onClick={() => {
                                                        const csvContent = "email\nemployee1@example.com\nemployee2@example.com\nemployee3@example.com";
                                                        const element = document.createElement("a");
                                                        element.setAttribute("href", "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent));
                                                        element.setAttribute("download", "sample_employees.csv");
                                                        element.style.display = "none";
                                                        document.body.appendChild(element);
                                                        element.click();
                                                        document.body.removeChild(element);
                                                    }}
                                                >
                                                    <img src={require("../../../images/download-icon-orange.png").default} alt="download" style={{ width: "16px", height: "16px", marginRight: "8px" }} />
                                                    {enumStrings.createPerk.downloadImportCsvSample}
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Enter Employees Email */}
                                        <div className={classes.emailInputContainer}>
                                            <div className={classes.emailInputLabel}>
                                                {enumStrings.createPerk.enterEmployeesEmail}
                                            </div>
                                            <div className={classes.emailInputWrapper}>
                                                <Autocomplete
                                                    multiple
                                                    id="privateEmails"
                                                    options={[]}
                                                    freeSolo
                                                    value={formik.values.privateEmails}
                                                    onChange={(_, newValue) => {
                                                        setPrivateEmails(newValue, (emailsToSet) =>
                                                            formik.setFieldValue("privateEmails", emailsToSet)
                                                        );
                                                    }}
                                                    renderTags={(value, getTagProps) =>
                                                        value.map((option, index) => {
                                                            const { onDelete, ...tagProps } = getTagProps({ index });
                                                            return (
                                                                <div
                                                                    key={`${option}-${index}`}
                                                                    {...tagProps}
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        border: "2px solid #1F1F1F",
                                                                        borderRadius: "10px",
                                                                        padding: "3px 6px 3px 8px",
                                                                        margin: "2px 6px 2px 0",
                                                                        background: "#FFFFFF",
                                                                        maxWidth: "100%",
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontSize: "14px",
                                                                            lineHeight: "1.1",
                                                                            color: "#3A3A3A",
                                                                            whiteSpace: "nowrap",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                        }}
                                                                    >
                                                                        {option}
                                                                    </span>
                                                                    <span
                                                                        onClick={(e) => onDelete?.(e)}
                                                                        style={{
                                                                            marginLeft: "8px",
                                                                            width: "20px",
                                                                            height: "20px",
                                                                            borderRadius: "50%",
                                                                            background: "#F21D2F",
                                                                            color: "#FFFFFF",
                                                                            display: "inline-flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            fontSize: "18px",
                                                                            lineHeight: 1,
                                                                            cursor: "pointer",
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        ×
                                                                    </span>
                                                                </div>
                                                            );
                                                        })
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            placeholder={enumStrings.createPerk.emailsWillAppear}
                                                            className={classes.emailInputField}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>

                                        {/* IMPORTANT NOTE Box */}
                                        <div className={classes.noteContainer}>
                                            <p className={classes.noteText}>
                                                {enumStrings.createPerk.importantNote}
                                            </p>
                                            <p className={classes.noteSubText} dangerouslySetInnerHTML={{ __html: enumStrings.createPerk.importantNoteRemove }} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Perk Redemption Availability Section */}
                            <div className={classes.fieldContainer}>
                                <div className={classes.fieldLabel}>
                                    {enumStrings.createPerk.perkRedemptionAvailabilityTitle}
                                    <span className={classes.requiredAsterisk}>*</span>
                                </div>
                                <p className={classes.helperText}>
                                    {enumStrings.createPerk.perkRedemptionAvailabilityHelperText}
                                </p>
                                <RadioGroup
                                    className={classes.twoColumnRadioGroup}
                                    value={formik.values.isReoccuring ? "reoccuring" : "one-time"}
                                    onChange={(e) => formik.setFieldValue("isReoccuring", e.target.value === "reoccuring")}
                                >
                                    <FormControlLabel
                                        value="reoccuring"
                                        control={<Radio color="primary" icon={<span style={{ width: 22, height: 22, border: "2px solid #CCCCCC", borderRadius: "50%", display: "inline-block", boxSizing: "border-box" }} />} checkedIcon={<span style={{ width: 22, height: 22, border: `2px solid ${theme.palette.primary.main}`, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.palette.primary.main, boxSizing: "border-box" }}><span style={{ width: 8, height: 8, backgroundColor: "#FFFFFF", borderRadius: "50%" }} /></span>} />}
                                        label={
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: "16px", color: "#000000" }}>
                                                    {enumStrings.createPerk.recurringPerkRedemption}
                                                </div>
                                                <div style={{ fontWeight: 400, fontSize: "14px", color: theme.palette.text.secondary, marginTop: "2px" }}>
                                                    {enumStrings.createPerk.recurringPerkDesc}
                                                </div>
                                            </div>
                                        }
                                        className={classes.radioLabel}
                                    />
                                    <FormControlLabel
                                        value="one-time"
                                        control={<Radio color="primary" icon={<span style={{ width: 22, height: 22, border: "2px solid #CCCCCC", borderRadius: "50%", display: "inline-block", boxSizing: "border-box" }} />} checkedIcon={<span style={{ width: 22, height: 22, border: `2px solid ${theme.palette.primary.main}`, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.palette.primary.main, boxSizing: "border-box" }}><span style={{ width: 8, height: 8, backgroundColor: "#FFFFFF", borderRadius: "50%" }} /></span>} />}
                                        label={
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: "16px", color: "#000000" }}>
                                                    {enumStrings.createPerk.oneTimePerkRedemption}
                                                </div>
                                                <div style={{ fontWeight: 400, fontSize: "14px", color: theme.palette.text.secondary, marginTop: "2px" }}>
                                                    {enumStrings.createPerk.oneTimePerkDesc}
                                                </div>
                                            </div>
                                        }
                                        className={classes.radioLabel}
                                    />
                                </RadioGroup>
                                {formik.submitCount > 0 && formik.errors.isReoccuring && (
                                    <div data-error="true" className={classes.redeemErrorText}>{formik.errors.isReoccuring}</div>
                                )}
                            </div>

                            {/* Only Offer Perk on Certain Days */}
                            <div className={classes.switchSection}>
                                <div className={classes.switchLabel}>{enumStrings.discountText.offersAvailableonCertainDaysText}</div>
                                <p className={classes.switchHelperText}>
                                    {enumStrings.createPerk.certainDaysHelperText}
                                </p>
                                <Switch
                                    checked={formik.values.isCertainDaysEnabled}
                                    onChange={(e) => {
                                        formik.setFieldValue("isCertainDaysEnabled", e.target.checked);
                                        if (!e.target.checked) formik.setFieldValue("daysAvailable", []);
                                    }}
                                    className={classes.customSwitch}
                                />
                                {formik.values.isCertainDaysEnabled && (
                                    <div className={classes.collapsibleContent}>
                                        <div className={classes.daysContainer}>
                                            {[
                                                { label: "Sun", value: DayEnum.Sunday },
                                                { label: "Mon", value: DayEnum.Monday },
                                                { label: "Tue", value: DayEnum.Tuesday },
                                                { label: "Wed", value: DayEnum.Wednesday },
                                                { label: "Thurs", value: DayEnum.Thursday },
                                                { label: "Fri", value: DayEnum.Friday },
                                                { label: "Sat", value: DayEnum.Saturday },
                                            ].map((day) => (
                                                <div key={day.value} className={classes.dayChip}>
                                                    <Checkbox
                                                        color="primary"
                                                        size="small"
                                                        checked={formik.values.daysAvailable?.includes(day.value)}
                                                        onChange={() => handleDayToggle(day.value)}
                                                    />
                                                    <Typography variant="caption">{day.label}</Typography>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Schedule Perk Display Date */}
                            <div className={classes.switchSection}>
                                <div className={classes.switchLabel}>{enumStrings.createPerk.schedulePerkDisplayDate}</div>
                                <p className={classes.switchHelperText}>
                                    {enumStrings.createPerk.schedulePerkHelperText}
                                </p>
                                <Switch
                                    checked={formik.values.isExpiryEnabled}
                                    onChange={(e) => {
                                        formik.setFieldValue("isExpiryEnabled", e.target.checked);
                                        if (!e.target.checked) {
                                            formik.setFieldValue("startDate", "");
                                            formik.setFieldValue("endDate", "");
                                        }
                                    }}
                                    className={classes.customSwitch}
                                />
                                {formik.values.isExpiryEnabled && (
                                    <div className={classes.collapsibleContent}>
                                        <div className={classes.dateRangeContainer}>
                                            {/* Start Date */}
                                            <div className={classes.dateField}>
                                                <div className={classes.dateFieldLabel}>{enumStrings.createPerk.startDate}</div>
                                                <MuiThemeProvider theme={goldDatePickerTheme}>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <MobileDatePicker
                                                            value={formik.values.startDate ? dayjs(formik.values.startDate) : null}
                                                            onChange={(newValue) => {
                                                                formik.setFieldValue("startDate", newValue ? newValue.format("YYYY-MM-DD") : "");
                                                            }}
                                                            format="MMM DD, YYYY"
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    variant: "outlined",
                                                                    placeholder: enumStrings.createPerk.clickToSetDate,
                                                                    InputProps: {
                                                                        style: {
                                                                                                        fontSize: "16px",
                                                                            height: "56px",
                                                                            backgroundColor: "#FFFFFF",
                                                                            borderRadius: "8px",
                                                                        },
                                                                    },
                                                                    sx: {
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                borderColor: "#000000",
                                                                            },
                                                                            "&.Mui-focused fieldset": {
                                                                                borderColor: theme.palette.primary.main,
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                                dialog: {
                                                                    sx: {
                                                                        "& .MuiPickersDay-root.Mui-selected": {
                                                                            backgroundColor: `${theme.palette.primary.main} !important`,
                                                                        },
                                                                        "& .MuiPickersDay-root:hover": {
                                                                            backgroundColor: "rgba(225, 167, 49, 0.1)",
                                                                        },
                                                                        "& .MuiPickersDay-today": {
                                                                            borderColor: `${theme.palette.primary.main} !important`,
                                                                        },
                                                                        "& .MuiButton-textPrimary": {
                                                                            color: `${theme.palette.primary.main} !important`,
                                                                        },
                                                                        "& .MuiPickersYear-yearButton.Mui-selected": {
                                                                            backgroundColor: `${theme.palette.primary.main} !important`,
                                                                        },
                                                                        "& .MuiPickersMonth-monthButton.Mui-selected": {
                                                                            backgroundColor: `${theme.palette.primary.main} !important`,
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </MuiThemeProvider>
                                                {formik.submitCount > 0 && formik.errors.startDate && (
                                                    <div data-error="true" className={classes.redeemErrorText}>{formik.errors.startDate}</div>
                                                )}
                                            </div>
                                            {/* End Date */}
                                            <div className={classes.dateField}>
                                                <div className={classes.dateFieldLabel}>{enumStrings.createPerk.endDate}</div>
                                                <MuiThemeProvider theme={goldDatePickerTheme}>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <MobileDatePicker
                                                            value={formik.values.endDate ? dayjs(formik.values.endDate) : null}
                                                            minDate={formik.values.startDate ? dayjs(formik.values.startDate).add(1, "day") : undefined}
                                                            onChange={(newValue) => {
                                                                formik.setFieldValue("endDate", newValue ? newValue.format("YYYY-MM-DD") : "");
                                                            }}
                                                            format="MMM DD, YYYY"
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    variant: "outlined",
                                                                    placeholder: enumStrings.createPerk.clickToSetDate,
                                                                    InputProps: {
                                                                        style: {
                                                                                                        fontSize: "16px",
                                                                            height: "56px",
                                                                            backgroundColor: "#FFFFFF",
                                                                            borderRadius: "8px",
                                                                        },
                                                                    },
                                                                    sx: {
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                borderColor: "#000000",
                                                                            },
                                                                            "&.Mui-focused fieldset": {
                                                                                borderColor: theme.palette.primary.main,
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                                dialog: {
                                                                    sx: {
                                                                        "& .MuiPickersDay-root.Mui-selected": {
                                                                            backgroundColor: `${theme.palette.primary.main} !important`,
                                                                        },
                                                                        "& .MuiPickersDay-root:hover": {
                                                                            backgroundColor: "rgba(225, 167, 49, 0.1)",
                                                                        },
                                                                        "& .MuiPickersDay-today": {
                                                                            borderColor: `${theme.palette.primary.main} !important`,
                                                                        },
                                                                        "& .MuiButton-textPrimary": {
                                                                            color: `${theme.palette.primary.main} !important`,
                                                                        },
                                                                        "& .MuiPickersYear-yearButton.Mui-selected": {
                                                                            backgroundColor: `${theme.palette.primary.main} !important`,
                                                                        },
                                                                        "& .MuiPickersMonth-monthButton.Mui-selected": {
                                                                            backgroundColor: `${theme.palette.primary.main} !important`,
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </MuiThemeProvider>
                                                {formik.submitCount > 0 && formik.errors.endDate && (
                                                    <div data-error="true" className={classes.redeemErrorText}>{formik.errors.endDate}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {showFutureStartDateNotification && (
                                    <div
                                        style={{
                                            background: theme.palette.text.primary,
                                            color: "#FFFFFF",
                                            padding: "14px 16px",
                                            borderRadius: "8px",
                                            marginTop: "10px",
                                
                                            fontSize: "14px",
                                            lineHeight: "1.5",
                                            width: "100%",
                                            boxSizing: "border-box",
                                        }}
                                    >
                                        A notification will not be sent for this perk because the current time does not match the availability day(s) and/or month(s) selected above.
                                    </div>
                                )}
                            </div>

                            {/* Where to Redeem Perk Section */}
                            <div className={classes.fieldContainer}>
                                <div className={classes.fieldLabel}>
                                    {enumStrings.createPerk.whereToRedeemPerk}
                                    <span className={classes.requiredAsterisk}>*</span>
                                </div>
                                <p className={classes.helperText}>
                                    {enumStrings.createPerk.whereToRedeemHelperText}
                                </p>
                                <RadioGroup
                                    className={classes.radioGroup}
                                    value={formik.values.redeemSettings?.whereToRedeem || WhereToRedeemEnum.InPerson}
                                    onChange={(e) => formik.setFieldValue("redeemSettings.whereToRedeem", e.target.value)}
                                >
                                    <FormControlLabel
                                        value={WhereToRedeemEnum.InPerson}
                                        control={<Radio color="primary" icon={<span style={{ width: 22, height: 22, border: "2px solid #CCCCCC", borderRadius: "50%", display: "inline-block", boxSizing: "border-box" }} />} checkedIcon={<span style={{ width: 22, height: 22, border: `2px solid ${theme.palette.primary.main}`, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.palette.primary.main, boxSizing: "border-box" }}><span style={{ width: 8, height: 8, backgroundColor: "#FFFFFF", borderRadius: "50%" }} /></span>} />}
                                        label={
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: "16px", color: "#000000" }}>
                                                    {enumStrings.createPerk.inPerson}
                                                </div>
                                                <div style={{ fontWeight: 400, fontSize: "14px", color: theme.palette.text.secondary, marginTop: "2px" }}>
                                                    {enumStrings.createPerk.inPersonDesc}
                                                </div>
                                            </div>
                                        }
                                        className={classes.radioLabel}
                                    />
                                    <FormControlLabel
                                        value={WhereToRedeemEnum.Online}
                                        control={<Radio color="primary" icon={<span style={{ width: 22, height: 22, border: "2px solid #CCCCCC", borderRadius: "50%", display: "inline-block", boxSizing: "border-box" }} />} checkedIcon={<span style={{ width: 22, height: 22, border: `2px solid ${theme.palette.primary.main}`, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.palette.primary.main, boxSizing: "border-box" }}><span style={{ width: 8, height: 8, backgroundColor: "#FFFFFF", borderRadius: "50%" }} /></span>} />}
                                        label={
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: "16px", color: "#000000" }}>
                                                    {enumStrings.createPerk.onlineLabel}
                                                </div>
                                                <div style={{ fontWeight: 400, fontSize: "14px", color: theme.palette.text.secondary, marginTop: "2px" }}>
                                                    {enumStrings.createPerk.onlineDesc}
                                                </div>
                                            </div>
                                        }
                                        className={classes.radioLabel}
                                    />
                                    <FormControlLabel
                                        value={WhereToRedeemEnum.ContactCompany}
                                        control={<Radio color="primary" icon={<span style={{ width: 22, height: 22, border: "2px solid #CCCCCC", borderRadius: "50%", display: "inline-block", boxSizing: "border-box" }} />} checkedIcon={<span style={{ width: 22, height: 22, border: `2px solid ${theme.palette.primary.main}`, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.palette.primary.main, boxSizing: "border-box" }}><span style={{ width: 8, height: 8, backgroundColor: "#FFFFFF", borderRadius: "50%" }} /></span>} />}
                                        label={
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: "16px", color: "#000000" }}>
                                                    {enumStrings.createPerk.contactCompanyLabel}
                                                </div>
                                                <div style={{ fontWeight: 400, fontSize: "14px", color: theme.palette.text.secondary, marginTop: "2px" }}>
                                                    {enumStrings.createPerk.contactCompanyDesc}
                                                </div>
                                            </div>
                                        }
                                        className={classes.radioLabel}
                                    />
                                </RadioGroup>
                                {formik.submitCount > 0 && getIn(formik.errors, "redeemSettings.whereToRedeem") && (
                                    <div data-error="true" className={classes.redeemErrorText}>{getIn(formik.errors, "redeemSettings.whereToRedeem")}</div>
                                )}

                                {formik.values.redeemSettings?.whereToRedeem === WhereToRedeemEnum.Online && (
                                    <div className={classes.redeemDetailsBox}>
                                        <div className={classes.redeemCardPill}>
                                            {enumStrings.createPerk.forOnlinePerkRedemptions}
                                        </div>
                                        <div className={classes.redeemFieldLabel}>
                                            {enumStrings.createPerk.discountCodeLabel} <span style={{ color: "red" }}>*</span>
                                        </div>
                                        <input
                                            className={`${classes.redeemInput} ${(formik.submitCount > 0 || getIn(formik.touched, "redeemSettings.howToRedeem.code")) && getIn(formik.errors, "redeemSettings.howToRedeem.code") ? classes.redeemInputError : ""}`}
                                            name="redeemSettings.howToRedeem.code"
                                            value={formik.values.redeemSettings.howToRedeem?.code || ""}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder={enumStrings.createPerk.enterDiscountCode}
                                        />
                                        {(formik.submitCount > 0 || getIn(formik.touched, "redeemSettings.howToRedeem.code")) && getIn(formik.errors, "redeemSettings.howToRedeem.code") && (
                                            <div data-error="true" className={classes.redeemErrorText}>{getIn(formik.errors, "redeemSettings.howToRedeem.code")}</div>
                                        )}
                                        <div className={classes.redeemFieldLabel} style={{ marginTop: "16px" }}>
                                            {enumStrings.createPerk.companyEcommerceUrl} <span style={{ color: "red" }}>*</span>
                                        </div>
                                        <input
                                            className={`${classes.redeemInput} ${(formik.submitCount > 0 || getIn(formik.touched, "redeemSettings.howToRedeem.websiteToEnterCodeAt")) && getIn(formik.errors, "redeemSettings.howToRedeem.websiteToEnterCodeAt") ? classes.redeemInputError : ""}`}
                                            name="redeemSettings.howToRedeem.websiteToEnterCodeAt"
                                            value={formik.values.redeemSettings.howToRedeem?.websiteToEnterCodeAt || ""}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder={enumStrings.createPerk.enterWebsiteUrl}
                                        />
                                        {(formik.submitCount > 0 || getIn(formik.touched, "redeemSettings.howToRedeem.websiteToEnterCodeAt")) && getIn(formik.errors, "redeemSettings.howToRedeem.websiteToEnterCodeAt") && (
                                            <div data-error="true" className={classes.redeemErrorText}>{getIn(formik.errors, "redeemSettings.howToRedeem.websiteToEnterCodeAt")}</div>
                                        )}
                                    </div>
                                )}

                                {formik.values.redeemSettings?.whereToRedeem === WhereToRedeemEnum.ContactCompany && (
                                    <div className={classes.redeemDetailsBox}>
                                        <div className={classes.redeemCardPill}>
                                            {enumStrings.createPerk.forContactCompanyRedemption}
                                        </div>
                                        <div className={classes.redeemFieldLabel}>
                                            {enumStrings.createPerk.departmentOrContactName} <span style={{ color: "red" }}>*</span>
                                        </div>
                                        <input
                                            className={`${classes.redeemInput} ${(formik.submitCount > 0 || getIn(formik.touched, "redeemSettings.howToRedeem.contactName")) && getIn(formik.errors, "redeemSettings.howToRedeem.contactName") ? classes.redeemInputError : ""}`}
                                            name="redeemSettings.howToRedeem.contactName"
                                            value={formik.values.redeemSettings.howToRedeem?.contactName || ""}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder={enumStrings.createPerk.enterDepartmentOrContactName}
                                        />
                                        {(formik.submitCount > 0 || getIn(formik.touched, "redeemSettings.howToRedeem.contactName")) && getIn(formik.errors, "redeemSettings.howToRedeem.contactName") && (
                                            <div data-error="true" className={classes.redeemErrorText}>{getIn(formik.errors, "redeemSettings.howToRedeem.contactName")}</div>
                                        )}
                                        <div className={classes.redeemFieldLabel} style={{ marginTop: "16px" }}>
                                            {enumStrings.createPerk.companyContactEmailLabel} <span style={{ color: "red" }}>*</span>
                                        </div>
                                        <input
                                            className={`${classes.redeemInput} ${(formik.submitCount > 0 || getIn(formik.touched, "redeemSettings.howToRedeem.contactEmail")) && getIn(formik.errors, "redeemSettings.howToRedeem.contactEmail") ? classes.redeemInputError : ""}`}
                                            name="redeemSettings.howToRedeem.contactEmail"
                                            value={formik.values.redeemSettings.howToRedeem?.contactEmail || ""}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder={enumStrings.createPerk.enterCompanyContactEmail}
                                        />
                                        {(formik.submitCount > 0 || getIn(formik.touched, "redeemSettings.howToRedeem.contactEmail")) && getIn(formik.errors, "redeemSettings.howToRedeem.contactEmail") && (
                                            <div data-error="true" className={classes.redeemErrorText}>{getIn(formik.errors, "redeemSettings.howToRedeem.contactEmail")}</div>
                                        )}
                                        <div className={classes.redeemFieldLabel} style={{ marginTop: "16px" }}>
                                            {enumStrings.createPerk.contactNumberLabel} <span style={{ color: "red" }}>*</span>
                                        </div>
                                        <NumberFormat
                                            format="(###) ###-####"
                                            className={`${classes.redeemInput} ${(formik.submitCount > 0 || getIn(formik.touched, "redeemSettings.howToRedeem.contactNumber")) && getIn(formik.errors, "redeemSettings.howToRedeem.contactNumber") ? classes.redeemInputError : ""}`}
                                            name="redeemSettings.howToRedeem.contactNumber"
                                            value={formik.values.redeemSettings.howToRedeem?.contactNumber || ""}
                                            onValueChange={(values) => {
                                                formik.setFieldValue("redeemSettings.howToRedeem.contactNumber", values.value);
                                            }}
                                            onBlur={formik.handleBlur}
                                            placeholder={enumStrings.createPerk.enterContactNumber}
                                        />
                                        {(formik.submitCount > 0 || getIn(formik.touched, "redeemSettings.howToRedeem.contactNumber")) && getIn(formik.errors, "redeemSettings.howToRedeem.contactNumber") && (
                                            <div data-error="true" className={classes.redeemErrorText}>{getIn(formik.errors, "redeemSettings.howToRedeem.contactNumber")}</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Send New Perk Notification */}
                            <div className={classes.switchSection} style={{ borderBottom: "none" }}>
                                <div className={classes.switchLabel}>{enumStrings.createPerk.sendNewPerkNotification}</div>
                                <p className={classes.switchHelperText}>
                                    {enumStrings.createPerk.sendNewPerkNotificationHelperText}
                                </p>
                                <Switch
                                    checked={formik.values.notify}
                                    onChange={(e) => formik.setFieldValue("notify", e.target.checked)}
                                    className={classes.customSwitch}
                                />
                            </div>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                className={classes.submitButton}
                                disabled={loader}
                            >
                                {loader ? <CircularProgress size={24} style={{ color: "white" }} /> : enumStrings.createPerk.createMemberPerkButton}
                            </Button>
                        </form >
                    </div >
                </div >
            </div >

            {/* IDA Generate Modal */}
            <IDAGenerateModal
                open={idaDialogOpen}
                onClose={() => setIdaDialogOpen(false)}
                keyDetails={idaKeyDetails}
                onKeyDetailsChange={setIdaKeyDetails}
                entityName={chamberMemberInfo?.name || "the company"}
                entityType="company"
                mode={idaFieldMeta.mode}
                maxLength={idaFieldMeta.maxLength}
                customTitle={idaFieldMeta.title}
                customSubtitle={idaFieldMeta.subtitle}
                customSubtitleLine2={idaFieldMeta.subtitleLine2}
                customPlaceholder={idaFieldMeta.placeholder}
                onGenerate={(generated) => {
                    const limited = generated.slice(0, idaFieldMeta.maxLength);
                    setIdaGeneratedText(limited);
                    setIdaDialogOpen(false);
                    setOverwriteDialogOpen(true);
                }}
            />

            {/* IDA Overwrite Confirmation Dialog */}
            <IDAOverwriteDialog
                open={overwriteDialogOpen}
                onClose={() => setOverwriteDialogOpen(false)}
                onRegenerate={() => {
                    setOverwriteDialogOpen(false);
                    setIdaDialogOpen(true);
                }}
                onConfirm={() => {
                    formik.setFieldValue(
                        currentIdaField,
                        idaGeneratedText.slice(0, idaFieldMeta.maxLength)
                    );
                    formik.setFieldError(currentIdaField, undefined);
                    formik.setFieldTouched(currentIdaField, true, false);
                    if (currentIdaField === "title") {
                        setIdaGeneratedTitle(true);
                    } else {
                        setIdaGeneratedDescription(true);
                    }
                    setOverwriteDialogOpen(false);
                    setIdaSuccessDialogOpen(true);
                    setTimeout(() => setIdaSuccessDialogOpen(false), 5000);
                }}
                title={idaFieldMeta.overwriteTitle}
                description={idaFieldMeta.overwriteDesc}
                generatedLabel={idaFieldMeta.generatedLabel}
                generatedText={idaGeneratedText}
            />

            {/* IDA Success Dialog */}
            <SuccessDialog
                open={idaSuccessDialogOpen}
                onClose={() => setIdaSuccessDialogOpen(false)}
                message={`${idaFieldMeta.successTitle} ${idaFieldMeta.successTitle2}`}
            />
        {!submittedSuccessfully && <UnsavedChangesPrompt isDirty={formik.dirty} />}
        </div >
    );
};

export default CreateMemberPerk;
